// src/pages/Brand/BrandPage.js
import { useState, useEffect } from 'react';
import BrandTable from './components/BrandTable';
import CategoryFilter from './components/CategoryFilter';
import { brandsAPI } from '../../api';
import './BrandPage.css';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * 브랜드 페이지 컴포넌트
 * 브랜드 목록과 필터링 기능을 제공합니다.
 */
const BrandPage = () => {
  const { translate, language } = useLanguage();

  // 카테고리 매핑
  const CATEGORY_MAP = {
    [translate('brandPage.categories.all')]: '',
    [translate('brandPage.categories.skincare')]: "CM01",
    [translate('brandPage.categories.bodycare')]: "CM02",
    [translate('brandPage.categories.haircare')]: "CM03",
    [translate('brandPage.categories.suncare')]: "CM04",
    [translate('brandPage.categories.cleansing')]: "CM05"
  };

  // 카테고리 배열
  const categories = [
    translate('brandPage.categories.all'),
    translate('brandPage.categories.skincare'),
    translate('brandPage.categories.bodycare'),
    translate('brandPage.categories.haircare'),
    translate('brandPage.categories.suncare'),
    translate('brandPage.categories.cleansing')
  ];

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [params, setParams] = useState({
    page: 1,
    perPage: 20,
    sortBy: 'search_trend',
    sortOrder: 'desc',
    category: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  /**
   * 브랜드 랭킹 데이터 조회
   */
  const fetchBrands = async () => {
    try {
      setLoading(true);

      // Updated to support Vietnamese language
      const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
      const apiParams = {
        ...params,
        lang: apiLanguage
      };

      const response = await brandsAPI.getBrandRankings(apiParams);

      setBrands(response.items);
      setPagination({
        total: response.total,
        totalPages: response.total_pages,
        hasNext: response.has_next,
        hasPrev: response.has_prev
      });
    } catch (error) {
      console.error(translate('brandPage.errorMessages.loadFailed'), error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 브랜드 검색
   */
  const handleSearch = async (searchTerm) => {
    try {
      setLoading(true);
      setIsSearchMode(true);
      setSearchTerm(searchTerm);

      const currentCategory = getCategoryName(params.category);
      const processedCategory = currentCategory === translate('brandPage.categories.all') ? "" : currentCategory;

      // Updated to support Vietnamese language
      const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
      const response = await brandsAPI.searchBrands(searchTerm, {
        sort_by: params.sortBy,
        category: processedCategory,
        lang: apiLanguage  // Add language parameter to search
      });

      setBrands(response.items);
      setPagination({
        total: response.total,
        totalPages: response.total_pages,
        hasNext: response.has_next,
        hasPrev: response.has_prev
      });
    } catch (error) {
      console.error(translate('brandPage.errorMessages.searchFailed'), error);
    } finally {
      setLoading(false);
    }
  };

  // 페이지 로드 시 및 파라미터 변경 시 데이터 조회
  useEffect(() => {
    if (!isSearchMode) {
      fetchBrands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, isSearchMode, language]); // Added language dependency

  /**
   * 카테고리 변경 처리
   */
  const handleCategoryChange = async (categoryName) => {
    if (isSearchMode) {
      try {
        setLoading(true);
        const processedCategory = categoryName === translate('brandPage.categories.all') ? "" : categoryName;

        setParams(prev => ({
          ...prev,
          category: CATEGORY_MAP[categoryName]
        }));

        // Updated to support Vietnamese language
        const apiLanguage = ['ko', 'en', 'vn'].includes(language) ? language : 'ko';
        const response = await brandsAPI.searchBrands(searchTerm, {
          sort_by: params.sortBy,
          category: processedCategory,
          lang: apiLanguage  // Add language parameter
        });

        setBrands(response.items);
        setPagination({
          total: response.total,
          totalPages: response.total_pages,
          hasNext: response.has_next,
          hasPrev: response.has_prev
        });
      } catch (error) {
        console.error(translate('brandPage.errorMessages.searchFailed'), error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setParams(prev => ({
      ...prev,
      page: 1,
      category: CATEGORY_MAP[categoryName]
    }));
  };

  /**
   * 정렬 변경 처리
   */
  const handleSort = async (sortField) => {
    if (isSearchMode) {
      try {
        setLoading(true);
        const currentCategory = getCategoryName(params.category);
        const processedCategory = currentCategory === translate('brandPage.categories.all') ? "" : currentCategory;

        setParams(prev => ({
          ...prev,
          sortBy: sortField,
          sortOrder: 'desc'
        }));

        // Updated to support Vietnamese language
        const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
        const response = await brandsAPI.searchBrands(searchTerm, {
          sort_by: sortField,
          category: processedCategory,
          lang: apiLanguage  // Add language parameter
        });

        setBrands(response.items);
        setPagination({
          total: response.total,
          totalPages: response.total_pages,
          hasNext: response.has_next,
          hasPrev: response.has_prev
        });
      } catch (error) {
        console.error(translate('brandPage.errorMessages.searchFailed'), error);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (params.sortBy === sortField) {
      return;
    }

    setParams(prev => ({
      ...prev,
      sortBy: sortField,
      sortOrder: 'desc'
    }));
  };

  /**
   * 페이지 변경 처리
   */
  const handlePageChange = (newPage) => {
    if (isSearchMode) return;

    // 페이지 변경
    setParams(prev => ({
      ...prev,
      page: newPage
    }));

    // 스크롤을 최상단으로 부드럽게 이동
    window.scrollTo({
      top: 0,
      behavior: 'smooth'  // 부드러운 스크롤 효과
    });
  };

  /**
   * 검색 초기화
   */
  const handleReset = () => {
    setIsSearchMode(false);
    setSearchTerm('');
    setParams(prev => ({
      ...prev,
      page: 1,
      sortBy: 'search_trend',
      sortOrder: 'desc',
      category: ''
    }));
  };

  /**
   * 카테고리 코드를 카테고리 이름으로 변환
   */
  const getCategoryName = (categoryCode) => {
    const categoryMap = {
      'CM01': translate('brandPage.categories.skincare'),
      'CM02': translate('brandPage.categories.bodycare'),
      'CM03': translate('brandPage.categories.haircare'),
      'CM04': translate('brandPage.categories.suncare'),
      'CM05': translate('brandPage.categories.cleansing'),
      '': translate('brandPage.categories.all')
    };
    return categoryMap[categoryCode] || translate('brandPage.categories.all');
  };

  /**
   * 페이지네이션 렌더링
   */
  const renderPagination = () => {
    const currentPage = params.page;
    const totalPages = pagination.totalPages;

    // 페이지 그룹의 시작과 끝 계산
    const pageGroup = Math.floor((currentPage - 1) / 10);
    const groupStart = pageGroup * 10 + 1;
    const groupEnd = Math.min(groupStart + 9, totalPages);

    return (
      <div className="pagination">
        {/* 이전 그룹 버튼 */}
        {groupStart > 1 && (
          <button
            className="pagination-button nav"
            onClick={() => handlePageChange(groupStart - 1)}
            aria-label="Previous page group"
          >
            {translate('brandPage.pagination.prev')}
          </button>
        )}

        {/* 페이지 번호 버튼들 */}
        {Array.from(
          { length: groupEnd - groupStart + 1 },
          (_, i) => groupStart + i
        ).map(page => (
          <button
            key={page}
            className={`pagination-button ${params.page === page ? 'active' : ''}`}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </button>
        ))}

        {/* 다음 그룹 버튼 */}
        {groupEnd < totalPages && (
          <button
            className="pagination-button nav"
            onClick={() => handlePageChange(groupEnd + 1)}
            aria-label="Next page group"
          >
            {translate('brandPage.pagination.next')}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="brand-page">
      <div className="brand-container">
        <div className="brand-page-header">
          <div className="content-wrapper">
            <span className="header-eyebrow">{translate('brandPage.header.eyebrow')}</span>
            <h1>{translate('brandPage.header.title')}</h1>
            <p>{translate('brandPage.header.subtitle')}</p>
          </div>
          <div className="icons-wrapper">
            <div className="decorative-icon" />
            <div className="decorative-icon" />
            <div className="decorative-icon" />
          </div>
        </div>

        <div className="brand-filter-section">
          <div className="brand-category-filter-group">
            {categories.map((category) => (
              <button
                key={category}
                className={`brand-category-filter-btn ${params.category === CATEGORY_MAP[category] ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        <CategoryFilter
          currentCategory={params.category}
          onCategoryChange={handleCategoryChange}
          onSearch={handleSearch}
          onReset={handleReset}
          searchTerm={searchTerm}
        />
        <BrandTable
          brands={brands}
          loading={loading}
          sortConfig={{ key: params.sortBy, direction: params.sortOrder }}
          onSort={handleSort}
          currentPage={params.page}
          perPage={params.perPage}
          isSearchMode={isSearchMode}
        />

        {!loading && !isSearchMode && renderPagination()}
      </div>
    </div>
  );
};

export default BrandPage;